import { Field, FormikProps } from 'formik';
import _ from 'lodash';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Select, TextField, VStack } from '@vivaahealth/design-system';

import { parseValue } from 'common/dist/utils/types';

import * as api from '../../../api';
import * as store from '../../../reducers/store';
import MailingAddressInput from '../../Guarantor/MailingAddressInput';

import { EditGuarantorValues } from './schema';
import { convertPlaceToAddress } from '../../AddressInput/utils';
import { getFormsFromFormSets } from '../../../utils/formSet';
import { getValueFromFieldOrPatient } from '../../utils';

const TEXT_FIELD_DATA: {
  fieldKey: keyof EditGuarantorValues;
}[] = [
  { fieldKey: 'firstName' },
  { fieldKey: 'lastName' },
  { fieldKey: 'homePhone' },
  { fieldKey: 'mobilePhone' },
];

interface Props {
  formikProps: FormikProps<EditGuarantorValues>;
}

export const EditGuarantorFormFields = ({ formikProps }: Props) => {
  const { t } = useTranslation('common');
  const { forms, patient, patientRelationshipsToGuarantor } = useSelector(
    (store: store.All) => ({
      forms: getFormsFromFormSets(store.formSets),
      patient: store.loggedInPatient,
      patientRelationshipsToGuarantor:
        store.masterPracticePreference.patientRelationshipsToGuarantor,
    }),
    _.isEqual,
  );

  const getPatientAddress = useCallback(() => {
    const fields = _.flatten(Object.values(forms).map((form) => form.fields));
    const patientPlaceDetails = parseValue<api.Places.PlaceDetails>(
      getValueFromFieldOrPatient(
        api.PrevisitFormTemplateFieldConfig.Primitive.PatientAddressMailing,
        fields,
        patient,
      ) || '',
    );
    return patientPlaceDetails && convertPlaceToAddress(patientPlaceDetails);
  }, [forms, patient]);

  return (
    <VStack>
      <VStack gap={24}>
        {TEXT_FIELD_DATA.map(({ fieldKey }) => (
          <Field
            as={TextField}
            key={fieldKey}
            label={t(fieldKey)}
            name={fieldKey}
            error={
              formikProps.touched[fieldKey] && formikProps.errors[fieldKey]
            }
          />
        ))}
        <MailingAddressInput
          label={t('address')}
          onChange={(address) => formikProps.setFieldValue('address', address)}
          value={formikProps.values.address ?? null}
          patientAddress={getPatientAddress()}
        />
      </VStack>
      <Select
        name="relationshipToPatient"
        label={t('relationshipToPatient')}
        value={{
          label: !!formikProps.values.relationshipToPatient
            ? t(
                api.Previsit.relationshipOptionsMap[
                  formikProps.values.relationshipToPatient
                ],
              )
            : '',
          value: formikProps.values.relationshipToPatient || '',
        }}
        error={
          !!formikProps.touched.relationshipToPatient
            ? formikProps.errors.relationshipToPatient
            : undefined
        }
        menuPlacement="top"
        onBlur={() =>
          formikProps.setFieldTouched('relationshipToPatient', true, false)
        }
        onChange={({ value: newRelationship }: any) => {
          formikProps.setFieldValue('relationshipToPatient', newRelationship);
        }}
        options={patientRelationshipsToGuarantor.relationships.map(
          (relationship: api.Common.Relationship) => ({
            label: t(api.Previsit.relationshipOptionsMap[relationship]),
            value: relationship,
          }),
        )}
      />
    </VStack>
  );
};
